import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import client, { getPin } from "@/lib/api";
import { TILL_LABEL, DEPARTMENTS } from "@/lib/constants";
import { formatDay, money } from "@/lib/format";
import { Download, AlertCircle, ArrowRight } from "lucide-react";
import { toast } from "sonner";

/** Departments-by-day breakdown. One or two rows per day (one per till). */
export default function Bridge() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [forbidden, setForbidden] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await client.get("/bridge", {
        params: { date_from: dateFrom || undefined, date_to: dateTo || undefined },
      });
      setItems(data.items || []);
      setForbidden(false);
    } catch (e) {
      if (e?.response?.status === 401 || e?.response?.status === 403) setForbidden(true);
      else toast.error("Couldn't load departments");
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  const downloadCsv = () => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/bridge/export.csv?pin=${encodeURIComponent(getPin())}`;
    const a = document.createElement("a");
    a.href = url; a.download = "till_trax_departments.csv"; a.rel = "noopener";
    document.body.appendChild(a); a.click();
    setTimeout(() => { try { document.body.removeChild(a); } catch { /* node already detached */ } }, 1000);
  };

  // Group rows by date (YYYY-MM-DD), keep till order Pro Shop then Grill.
  const days = useMemo(() => {
    const map = new Map();
    for (const r of items) {
      const day = (r.created_at || "").slice(0, 10);
      if (!map.has(day)) map.set(day, { day, rows: [] });
      map.get(day).rows.push(r);
    }
    for (const d of map.values()) {
      d.rows.sort((a, b) => (a.till === "Pro Shop" ? -1 : 1) - (b.till === "Pro Shop" ? -1 : 1));
    }
    return Array.from(map.values());
  }, [items]);

  // Totals across the filter window per till, using each till's own schema.
  const totals = useMemo(() => {
    const out = { proShop: {}, grill: {}, proShopDrawer: 0, grillDrawer: 0 };
    for (const d of (DEPARTMENTS["Pro Shop"] || [])) out.proShop[d.key] = 0;
    for (const d of (DEPARTMENTS["Grill"] || []))    out.grill[d.key]   = 0;
    for (const r of items) {
      if (r.till !== "Pro Shop" && r.till !== "Grill") continue; // Masters Bar not part of the bridge
      const bucket = r.till === "Pro Shop" ? "proShop" : "grill";
      out[`${bucket}Drawer`] += Number(r.drawer_total || 0);
      const gt = r.groups || {};
      const schema = DEPARTMENTS[r.till] || [];
      for (const d of schema) out[bucket][d.key] = (out[bucket][d.key] || 0) + Number(gt[d.key] || 0);
    }
    return out;
  }, [items]);

  if (forbidden) {
    return (
      <div className="min-h-screen bg-[#FDFBF7]">
        <Header onBack={() => nav(-1)} />
        <main className="max-w-md mx-auto px-5 py-10" data-testid="bridge-forbidden">
          <div className="bg-white border border-[#E8E4DA] rounded-2xl p-6 text-center">
            <AlertCircle className="mx-auto text-[#D97706] mb-3" />
            <h2 className="text-xl text-[#0D311E]" style={{ fontFamily: "'Fraunces', serif" }}>Owner PIN required</h2>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7]" style={{ fontFamily: "'Hanken Grotesk', sans-serif" }}>
      <Header title="Departments by day" onBack={() => nav("/dashboard")} />
      <main className="max-w-md mx-auto px-5 py-5 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl text-[#0D311E]" style={{ fontFamily: "'Fraunces', serif" }}>Department breakdown</h2>
            <p className="text-xs text-[#5C7566]">One row per till per day. Both tills cross-sell — keep them side by side.</p>
          </div>
          <button
            data-testid="export-bridge-csv"
            onClick={downloadCsv}
            className="h-10 px-3 rounded-xl bg-[#1A5D3A] text-white text-sm flex items-center gap-2 active:scale-95"
          >
            <Download size={14} /> Excel
          </button>
        </div>

        <div className="bg-white border border-[#E8E4DA] rounded-2xl p-4 grid grid-cols-2 gap-2">
          <input
            data-testid="bridge-from"
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="h-11 rounded-xl border border-[#E8E4DA] bg-white px-3 text-sm text-[#0D311E] outline-none focus:border-[#1A5D3A]"
          />
          <input
            data-testid="bridge-to"
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="h-11 rounded-xl border border-[#E8E4DA] bg-white px-3 text-sm text-[#0D311E] outline-none focus:border-[#1A5D3A]"
          />
        </div>

        {(items.length > 0) && (
          <div className="bg-[#0D311E] text-[#FDFBF7] rounded-2xl p-4" data-testid="bridge-totals">
            <div className="text-[10px] tracking-[0.22em] uppercase text-[#FDFBF7]/70 mb-2">Totals in range</div>
            <TotalsRow label="Pro Shop" drawer={totals.proShopDrawer} groups={totals.proShop} schema={DEPARTMENTS["Pro Shop"]} />
            <div className="h-px bg-[#FDFBF7]/15 my-2" />
            <TotalsRow label="Grill"    drawer={totals.grillDrawer}    groups={totals.grill}    schema={DEPARTMENTS["Grill"]} />
          </div>
        )}

        {loading ? (
          <div className="text-center text-[#5C7566] py-10">Loading…</div>
        ) : days.length === 0 ? (
          <div data-testid="bridge-empty" className="text-center text-[#5C7566] py-10 bg-white border border-[#E8E4DA] rounded-2xl">
            No closes in range yet.
          </div>
        ) : (
          <div className="space-y-3" data-testid="bridge-list">
            {days.map((d) => <DayCard key={d.day} day={d} onOpen={(id) => nav(`/close-detail/${id}`)} />)}
          </div>
        )}
      </main>
    </div>
  );
}

function DayCard({ day, onOpen }) {
  return (
    <section className="bg-white border border-[#E8E4DA] rounded-2xl p-4">
      <div className="text-[#0D311E] font-medium" style={{ fontFamily: "'Fraunces', serif" }}>
        {formatDay(`${day.day}T12:00:00Z`)}
      </div>
      <div className="mt-3 space-y-2">
        {day.rows.map((r) => {
          const schema = DEPARTMENTS[r.till] || [];
          return (
            <button
              key={r.id}
              data-testid={`bridge-row-${r.id}`}
              onClick={() => onOpen(r.id)}
              className="w-full text-left rounded-xl border border-[#E8E4DA] p-3 hover:border-[#1A5D3A]"
            >
              <div className="flex items-center justify-between">
                <span className="text-[#0D311E] font-medium">{TILL_LABEL[r.till] || r.till}</span>
                <span className="tabular-nums text-sm text-[#5C7566]">Drawer {money(r.drawer_total)}</span>
              </div>
              <div className="mt-2 grid grid-cols-3 gap-1.5 text-[11px]">
                {schema.map((d) => {
                  const v = Number((r.groups || {})[d.key] || 0);
                  return (
                    <div key={d.key} className={`rounded-lg p-2 ${v > 0 ? "bg-[#F5F2EA]" : "bg-[#FDFBF7] border border-dashed border-[#E8E4DA]"}`}>
                      <div className="text-[9px] tracking-[0.18em] uppercase text-[#5C7566]">{d.label}</div>
                      <div className={`tabular-nums ${v > 0 ? "text-[#0D311E]" : "text-[#5C7566]/50"}`}>{money(v)}</div>
                    </div>
                  );
                })}
              </div>
            </button>
          );
        })}
      </div>
    </section>
  );
}

function TotalsRow({ label, drawer, groups, schema }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-[11px] tracking-[0.18em] uppercase text-[#FDFBF7]/70">{label}</span>
        <span className="tabular-nums text-sm">{money(drawer)}</span>
      </div>
      <div className="grid grid-cols-3 gap-1.5 text-[11px]">
        {schema.map((d) => (
          <div key={d.key} className="bg-[#FDFBF7]/10 rounded-lg p-2">
            <div className="text-[9px] tracking-[0.18em] uppercase text-[#FDFBF7]/60">{d.label}</div>
            <div className="tabular-nums">{money(groups[d.key] || 0)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
